/* eslint-disable no-empty-pattern */
import * as React from 'react';
import { useSubscription } from '@apollo/client';
import { GET_CABINET_STATUS, TCabinetGetStatusResult, TGetCabinetStatusOutput, TGetCabinetDataInput } from '../../../../graphql/schema';
import { CabinetContext } from '../../../../context/CabinetContext';

interface IIncidentProps {
}

const Incident: React.FunctionComponent<IIncidentProps> = (props) => {
    const { selectedDevice } = React.useContext(CabinetContext)!
    const [cabinetStatusData, setCabinetStatusData] = React.useState<TCabinetGetStatusResult | null>(null)
    useSubscription<TGetCabinetStatusOutput, TGetCabinetDataInput>(GET_CABINET_STATUS, {
        variables: {
            cabinetDeviceId: selectedDevice!.id
        },
        onData(d) {
            const result = d.data.data?.getCabinetStatus
            if (result) {
                setCabinetStatusData(result)
            }
        }
    });

    // Helper function to check if connection is lost (2 means disconnected, 0 means no device)
    const isConnectionLost = (status: number) => status === 2;

    // Get active incidents with enhanced data
    const getActiveIncidents = () => {
        if (!cabinetStatusData) return [];

        const incidents = [];

        if (isConnectionLost(cabinetStatusData.pmConnect)) {
            incidents.push({
                id: 'pm',
                text: 'Mất kết nối PM',
                icon: '⚠️',
                severity: 'high',
                description: 'Đồng hồ điện không phản hồi'
            });
        }

        if (isConnectionLost(cabinetStatusData.lcConnect)) {
            incidents.push({
                id: 'lc',
                text: 'Mất kết nối LC',
                icon: '🔌',
                severity: 'medium',
                description: 'Thiết bị đo dòng rò không phản hồi'
            });
        }

        if (isConnectionLost(cabinetStatusData.plcConnect)) {
            incidents.push({
                id: 'plc',
                text: 'Mất kết nối PLC',
                icon: '⚡',
                severity: 'high',
                description: 'Bộ điều khiển logic lập trình PLC không hoạt động'
            });
        }

        if (isConnectionLost(cabinetStatusData.slgConnect)) {
            incidents.push({
                id: 'slg',
                text: 'Mất kết nối điểm đèn',
                icon: '💡',
                severity: 'high',
                description: 'Điểm đèn không phản hồi tín hiệu'
            });
        }

        return incidents;
    };

    const activeIncidents = getActiveIncidents();

    if (activeIncidents.length === 0) {
        return (
            <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 shadow-sm">
                <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-2xl">✅</span>
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-green-800 mb-1">
                            Tất cả hệ thống hoạt động bình thường
                        </h3>
                        <p className="text-green-600 text-sm">
                            Không có sự cố nào đang xảy ra
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl border border-red-100 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-4">
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                        <span className="text-white text-lg">🚨</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-bold text-white">
                            Sự cố đang xảy ra
                        </h3>
                        <p className="text-red-100 text-sm">
                            {activeIncidents.length} vấn đề cần được xử lý
                        </p>
                    </div>
                </div>
            </div>

            {/* Incidents List */}
            <div className="px-6 pb-6 space-y-4">
                {activeIncidents.map((incident, index) => (
                    <div
                        key={incident.id}
                        className={`group relative p-4 bg-gradient-to-r from-red-50 to-orange-50 border border-red-200 rounded-lg hover:shadow-md transition-all duration-300 transform hover:-translate-y-1`}
                        style={{
                            animationDelay: `${index * 100}ms`,
                            animation: 'slideInUp 0.5s ease-out forwards',
                            borderLeft: incident.severity === 'high' ? '4px solid #FF0000' : '4px solid #FF8800'
                        }}
                    >
                        {/* Pulse indicator for high severity */}
                        {incident.severity === 'high' && (
                            <div className="absolute top-3 right-3">
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                            </div>
                        )}

                        <div className="flex items-start space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl`}
                                style={{
                                    backgroundColor: incident.severity === 'high' ? '#FFE3E3' : '#FFE5B4',
                                    color: incident.severity === 'high' ? '#FF0000' : '#FF8800'
                                }}
                            >
                                {incident.icon}
                            </div>

                            <div className="flex-1 min-w-0">
                                <h4 className={`font-semibold text-sm mb-1 ${incident.severity === 'high' ? 'text-red-800' : 'text-orange-800'
                                    }`}>
                                    {incident.text}
                                </h4>
                                <p className="text-gray-600 text-xs leading-relaxed">
                                    {incident.description}
                                </p>

                                {/* Status indicator */}
                                <div className="mt-3">
                                    <div className={`inline-flex items-center px-3 py-2 rounded-lg shadow-sm border`} style={{
                                        backgroundColor: incident.severity === 'high' ? '#FFE3E3' : '#FFE5B4',
                                        color: incident.severity === 'high' ? '#FF0000' : '#FF8800'
                                    }}>
                                        <span className="text-xs font-semibold">
                                            {incident.severity === 'high' ? 'Nghiêm trọng' : 'Trung bình'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
                <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Cập nhật lần cuối: {new Date().toLocaleTimeString('vi-VN')}</span>
                    <span className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span>Đang theo dõi</span>
                    </span>
                </div>
            </div>

            <style>{`
                @keyframes slideInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default Incident;
